import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { resolveTheme } from "@/components/public-invoice/themes";
import { InvoiceHeader } from "@/components/public-invoice/InvoiceHeader";
import { InvoiceMeta } from "@/components/public-invoice/InvoiceMeta";
import { InvoiceItemsTable } from "@/components/public-invoice/InvoiceItemsTable";
import { InvoiceTotals } from "@/components/public-invoice/InvoiceTotals";
import { PaidBanner, PendingBanner } from "@/components/public-invoice/StatusBanners";
import { PayWalletSection } from "@/components/public-invoice/PayWalletSection";
import { ShareInvoice } from "@/components/public-invoice/ShareInvoice";

const PublicInvoice = () => {
  const { invoiceId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/public/invoices/${invoiceId}`);
      setData(r.data);
    } catch (e) {
      setErr("Invoice not found");
    }
  };

  useEffect(() => {
    load();
  }, [invoiceId]); // eslint-disable-line

  const themeName = (data?.creator?.invoice_theme || "mono").toLowerCase();
  const theme = useMemo(() => resolveTheme(themeName), [themeName]);
  const brand = data?.creator?.brand_color || "#ffffff";

  if (err)
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400"
        data-testid="public-invoice-error"
      >
        {err}
      </div>
    );
  if (!data)
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400">
        Loading…
      </div>
    );

  const inv = data.invoice;
  const address = data.creator.crypto_address || "";
  const isPaid = inv.status === "paid";
  const isPending = inv.status === "pending_confirmation";
  const isFree = (data.creator?.subscription_tier || "free").toLowerCase() === "free";

  const fontFamilyStyle = theme.fontFamily ? { fontFamily: theme.fontFamily } : undefined;

  return (
    <div
      className={`min-h-screen ${theme.page}`}
      style={fontFamilyStyle}
      data-testid="public-invoice-page"
      data-theme={themeName}
    >
      <div className="max-w-2xl mx-auto px-6 py-16">
        {theme.bigAccentBar && (
          <div className="h-2 w-full mb-10" style={{ background: brand }} data-testid="theme-accent-bar" />
        )}

        <InvoiceHeader
          creator={data.creator}
          invoice={inv}
          theme={theme}
          themeName={themeName}
          brand={brand}
        />

        <InvoiceMeta creator={data.creator} invoice={inv} theme={theme} />

        <InvoiceItemsTable invoice={inv} theme={theme} themeName={themeName} brand={brand} />

        <InvoiceTotals invoice={inv} theme={theme} themeName={themeName} brand={brand} />

        {isPaid && <PaidBanner theme={theme} themeName={themeName} brand={brand} />}
        {isPending && <PendingBanner theme={theme} />}

        {!isPaid && !isPending && (
          <PayWalletSection
            invoice={inv}
            address={address}
            theme={theme}
            brand={brand}
            onPaymentMarked={load}
          />
        )}

        {inv.notes && <div className={`mt-10 text-sm ${theme.notesCls}`}>{inv.notes}</div>}

        <ShareInvoice invoice={inv} creator={data.creator} theme={theme} />

        {/* Drafted with Quill — Free tier only, both on web + already in PDF */}
        {isFree && (
          <div
            className={`mt-16 text-center text-xs ${theme.footerCls}`}
            data-testid="quill-watermark"
          >
            Drafted with{" "}
            <a
              href={
                data.creator?.referral_code
                  ? `https://quillpayed.com/?ref=${data.creator.referral_code}`
                  : "https://quillpayed.com"
              }
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-80"
              data-testid="quill-watermark-link"
            >
              Quill · quillpayed.com
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicInvoice;
